/**
 * IngredientDetail — full encyclopedia entry.
 *
 * Route: /food-pulse/encyclopedia/:slug
 * Backend: /api/ingredients/:slug
 *
 * Renders all 20+ structured sections from the JSON payload with a
 * sticky table of contents.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft, Loader2, Beaker, ChevronRight, Info, ExternalLink,
  BookOpen, ShieldCheck, AlertTriangle,
} from "lucide-react";
import SeoHead from "@/components/SeoHead";
import FarmersPostingStrip from "@/components/FarmersPostingStrip";
import JsonLd, { breadcrumbSchema } from "@/components/JsonLd";

const API = process.env.REACT_APP_BACKEND_URL;
const GREEN = "#0F3D2E";
const AMBER = "#E4A140";
const CREAM = "#FBF7EF";

const SECTIONS = [
  { id: "summary",              title: "Summary" },
  { id: "quick-facts",          title: "Quick facts" },
  { id: "chemical-structure",   title: "Chemical structure" },
  { id: "manufacturing",        title: "Manufacturing" },
  { id: "history",              title: "History" },
  { id: "why-used",             title: "Why food companies use it" },
  { id: "common-foods",         title: "Common foods containing it" },
  { id: "health-benefits",      title: "Health benefits" },
  { id: "possible-risks",       title: "Possible health risks" },
  { id: "safe-intake",          title: "Safe intake (ADI)" },
  { id: "regulatory",           title: "Regulatory status worldwide" },
  { id: "scientific-research",  title: "Scientific research" },
  { id: "public-controversies", title: "Public controversies" },
  { id: "environmental-impact", title: "Environmental impact" },
  { id: "occupational",         title: "Occupational exposure" },
  { id: "animal-studies",       title: "Animal studies" },
  { id: "human-studies",        title: "Human clinical studies" },
  { id: "labeling",             title: "Food labeling" },
  { id: "natural-sources",      title: "Natural sources" },
  { id: "myths",                title: "Common myths" },
  { id: "faqs",                 title: "FAQs" },
  { id: "related-ingredients",  title: "Related ingredients" },
  { id: "related-foods",        title: "Related food items" },
  { id: "references",           title: "References" },
];

export default function IngredientDetail() {
  const { slug } = useParams();
  const [d, setD] = useState(null);
  const [state, setState] = useState("loading");   // loading | ok | not-found | error

  useEffect(() => {
    setState("loading");
    axios.get(`${API}/api/ingredients/${slug}`)
      .then((r) => { setD(r.data); setState("ok"); })
      .catch((e) => setState(e?.response?.status === 404 ? "not-found" : "error"));
  }, [slug]);

  const availableSections = useMemo(() => {
    if (!d) return [];
    return SECTIONS.filter((s) => sectionHasContent(s.id, d));
  }, [d]);

  if (state === "loading") {
    return <div className="min-h-screen flex items-center justify-center" style={{ background: CREAM }}>
      <Loader2 className="w-10 h-10 animate-spin opacity-40" data-testid="ingredient-loading" />
    </div>;
  }
  if (state === "not-found") {
    return <NotFound slug={slug} />;
  }
  if (state === "error" || !d) {
    return <div className="min-h-screen flex items-center justify-center p-8" style={{ background: CREAM, color: GREEN }}>
      <div className="text-center" data-testid="ingredient-error">
        <AlertTriangle className="w-12 h-12 mx-auto opacity-40 mb-3" />
        <div className="font-display text-xl">Couldn't load this ingredient. Try again in a moment.</div>
      </div>
    </div>;
  }

  const qf = d.quick_facts || {};
  const reg = d.regulatory_status || {};

  return (
    <div className="min-h-screen" style={{ background: CREAM, color: GREEN }} data-testid="ingredient-detail-page">
      <SeoHead section="food-pulse"
               title={(d.seo?.title || `${d.name} — Food Ingredient Encyclopedia`)}
               description={(d.seo?.meta_description || (d.summary || "").replace(/<[^>]+>/g, "").slice(0, 155))} />

      {/* Hero */}
      <section className="border-b" style={{ borderColor: `${AMBER}55`, background: "#fff" }}>
        <div className="max-w-5xl mx-auto px-4 py-10 sm:py-12">
          <Link to="/food-pulse/encyclopedia" className="inline-flex items-center gap-1 text-xs opacity-70 hover:opacity-100 mb-3" data-testid="ingredient-back">
            <ArrowLeft className="w-3 h-3" /> Encyclopedia
          </Link>
          <div className="inline-flex items-center gap-2 rounded-full border px-3 py-1 text-[11px] font-mono uppercase tracking-widest mb-3"
               style={{ borderColor: AMBER, color: AMBER }}>
            <Beaker className="w-3.5 h-3.5" />
            {d.category || "Ingredient"}
            {d.e_number && <span>· {d.e_number}</span>}
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-tight" data-testid="ingredient-name">
            {d.name}
          </h1>
          {d.scientific_name && (
            <div className="mt-2 italic opacity-70 text-sm">{d.scientific_name}</div>
          )}
          {(d.alternative_names || []).length > 0 && (
            <div className="mt-3 text-xs opacity-70">
              <span className="uppercase tracking-widest opacity-60 mr-1">Also known as:</span>
              {d.alternative_names.join(" · ")}
            </div>
          )}
          {d.chemical_formula && (
            <div className="mt-1 text-xs font-mono opacity-70">
              <span className="uppercase tracking-widest opacity-60 mr-1">Formula:</span>
              {d.chemical_formula}
            </div>
          )}
          {d.wikimedia_structure_url && (
            <div className="mt-5 max-w-xs rounded-xl border p-3 bg-white" style={{ borderColor: `${AMBER}55` }}>
              <img src={d.wikimedia_structure_url} alt={`${d.name} molecular structure`}
                   className="w-full max-h-48 object-contain mx-auto" loading="lazy" />
              <div className="text-[10px] opacity-50 mt-1 text-center">Wikimedia Commons</div>
            </div>
          )}
        </div>
      </section>

      {/* Safety callout — surfaces above-the-fold when the ingredient
          has a known interaction, restriction, or public-health advisory
          that the reader should see before scrolling further. */}
      {d.safety_alert && <SafetyCallout alert={d.safety_alert} />}

      {/* FarmersPulse cross-site strip — active farmer crop-posts for this
          ingredient. Renders nothing when no farm is currently posting. */}
      <div className="max-w-5xl mx-auto px-4 pt-6">
        <FarmersPostingStrip slug={slug} />
      </div>

      <div className="max-w-6xl mx-auto px-4 py-10 grid gap-8 lg:grid-cols-[220px_1fr]">
        {/* Sticky TOC */}
        <aside>
          <nav className="lg:sticky lg:top-24 text-sm" data-testid="ingredient-toc">
            <div className="text-[10px] font-mono uppercase tracking-widest mb-2 opacity-60">On this page</div>
            <ul className="space-y-1">
              {availableSections.map((s) => (
                <li key={s.id}>
                  <a href={`#${s.id}`} className="opacity-70 hover:opacity-100 hover:underline"
                     data-testid={`toc-${s.id}`}>
                    {s.title}
                  </a>
                </li>
              ))}
            </ul>
          </nav>
        </aside>

        {/* Body */}
        <article className="space-y-10 max-w-3xl">
          {d.summary && <Section id="summary" title="Summary" body={d.summary} />}

          {Object.values(qf).some(Boolean) && (
            <section id="quick-facts" className="scroll-mt-24" data-testid="section-quick-facts">
              <h2 className="font-display text-2xl mb-4">Quick facts</h2>
              <dl className="grid gap-2 sm:grid-cols-2 rounded-2xl border p-5 bg-white" style={{ borderColor: `${AMBER}55` }}>
                {[
                  ["Category", qf.chemical_class || d.category],
                  ["Origin", qf.origin],
                  ["Colour", qf.colour],
                  ["Taste", qf.taste],
                  ["Solubility", qf.solubility],
                  ["Molecular weight", qf.molecular_weight],
                  ["pH", qf.ph],
                  ["Melting point", qf.melting_point],
                  ["Stability", qf.stability],
                  ["Shelf life", qf.shelf_life],
                  ["Typical concentration", qf.typical_concentration],
                  ["Regulatory status", qf.regulatory_status],
                  ["First commercial use", qf.first_commercial_use],
                ].filter(([, v]) => v && String(v).trim()).map(([k, v]) => (
                  <div key={k} className="flex flex-col text-xs">
                    <dt className="opacity-60 font-mono uppercase tracking-widest">{k}</dt>
                    <dd className="mt-0.5">{v}</dd>
                  </div>
                ))}
              </dl>
            </section>
          )}

          {d.chemical_structure    && <Section id="chemical-structure" title="Chemical structure" body={d.chemical_structure} />}
          {d.manufacturing_process && <Section id="manufacturing"       title="Manufacturing"        body={d.manufacturing_process} />}
          {d.history               && <Section id="history"             title="History"              body={d.history} />}
          {d.why_used              && <Section id="why-used"            title="Why food companies use it" body={d.why_used} />}

          {(d.common_foods || []).length > 0 && (
            <section id="common-foods" className="scroll-mt-24" data-testid="section-common-foods">
              <h2 className="font-display text-2xl mb-4">Common foods containing it</h2>
              <div className="flex flex-wrap gap-2">
                {d.common_foods.map((f) => (
                  <span key={f} className="rounded-full px-3 py-1 text-xs" style={{ background: "#fff", border: `1px solid ${AMBER}55` }}>{f}</span>
                ))}
              </div>
            </section>
          )}

          {d.health_benefits && <Section id="health-benefits" title="Health benefits" body={d.health_benefits} icon={ShieldCheck} />}
          {d.possible_risks  && <Section id="possible-risks"  title="Possible health risks" body={d.possible_risks} icon={AlertTriangle} />}
          {d.safe_intake     && <Section id="safe-intake"     title="Safe intake (ADI)" body={d.safe_intake} />}

          {Object.values(reg).some(Boolean) && (
            <section id="regulatory" className="scroll-mt-24" data-testid="section-regulatory">
              <h2 className="font-display text-2xl mb-4">Regulatory status worldwide</h2>
              <dl className="grid gap-2 sm:grid-cols-2 rounded-2xl border p-5 bg-white" style={{ borderColor: `${AMBER}55` }}>
                {[
                  ["FDA (USA)", reg.fda], ["EFSA (EU)", reg.efsa], ["FSANZ (AU/NZ)", reg.fsanz],
                  ["Health Canada", reg.health_canada], ["Codex Alimentarius", reg.codex],
                ].filter(([, v]) => v && String(v).trim()).map(([k, v]) => (
                  <div key={k} className="text-xs">
                    <dt className="opacity-60 font-mono uppercase tracking-widest">{k}</dt>
                    <dd className="mt-0.5">{v}</dd>
                  </div>
                ))}
                {(reg.banned_or_restricted_in || []).length > 0 && (
                  <div className="text-xs sm:col-span-2">
                    <dt className="opacity-60 font-mono uppercase tracking-widest">Banned / restricted in</dt>
                    <dd className="mt-0.5">{reg.banned_or_restricted_in.join(" · ")}</dd>
                  </div>
                )}
              </dl>
            </section>
          )}

          {d.scientific_research  && <Section id="scientific-research"  title="Scientific research"      body={d.scientific_research} />}
          {d.public_controversies && <Section id="public-controversies" title="Public controversies"     body={d.public_controversies} />}
          {d.environmental_impact && <Section id="environmental-impact" title="Environmental impact"     body={d.environmental_impact} />}
          {d.occupational_exposure&& <Section id="occupational"         title="Occupational exposure"    body={d.occupational_exposure} />}
          {d.animal_studies       && <Section id="animal-studies"       title="Animal studies"           body={d.animal_studies} />}
          {d.human_studies        && <Section id="human-studies"        title="Human clinical studies"   body={d.human_studies} />}
          {d.food_labeling        && <Section id="labeling"             title="Food labeling"            body={d.food_labeling} />}
          {d.natural_sources      && <Section id="natural-sources"      title="Natural sources"          body={d.natural_sources} />}

          {(d.common_myths || []).length > 0 && (
            <section id="myths" className="scroll-mt-24" data-testid="section-myths">
              <h2 className="font-display text-2xl mb-4">Common myths</h2>
              <div className="space-y-3">
                {d.common_myths.map((m, i) => (
                  <div key={i} className="rounded-xl border p-4 bg-white" style={{ borderColor: `${AMBER}55` }}>
                    <div className="text-xs font-mono uppercase tracking-widest opacity-60">Myth</div>
                    <div className="text-sm">{m.myth}</div>
                    <div className="text-xs font-mono uppercase tracking-widest opacity-60 mt-2">Fact</div>
                    <div className="text-sm" dangerouslySetInnerHTML={{ __html: m.fact }} />
                  </div>
                ))}
              </div>
            </section>
          )}

          {(d.faqs || []).length > 0 && (
            <section id="faqs" className="scroll-mt-24" data-testid="section-faqs">
              <h2 className="font-display text-2xl mb-4">FAQs</h2>
              <div className="space-y-2">
                {d.faqs.map((f, i) => (
                  <details key={i} className="rounded-xl border bg-white p-4" style={{ borderColor: `${AMBER}55` }} data-testid={`faq-${i}`}>
                    <summary className="cursor-pointer font-medium text-sm">{f.q}</summary>
                    <div className="mt-2 text-sm opacity-90" dangerouslySetInnerHTML={{ __html: f.a }} />
                  </details>
                ))}
              </div>
            </section>
          )}

          {(d.related_ingredients_full || []).length > 0 && (
            <section id="related-ingredients" className="scroll-mt-24" data-testid="section-related-ingredients">
              <h2 className="font-display text-2xl mb-4">Related ingredients</h2>
              <div className="grid gap-2 sm:grid-cols-2">
                {d.related_ingredients_full.map((r) => (
                  <Link key={r.slug} to={`/food-pulse/encyclopedia/${r.slug}`}
                        className="rounded-xl border p-3 bg-white hover:shadow-md transition-all flex items-center justify-between gap-2"
                        style={{ borderColor: `${AMBER}55` }}
                        data-testid={`related-ing-${r.slug}`}>
                    <div className="min-w-0">
                      <div className="font-medium text-sm">{r.name}</div>
                      <div className="text-[10px] font-mono uppercase tracking-widest opacity-60">{r.category}</div>
                    </div>
                    <ChevronRight className="w-4 h-4 opacity-40 flex-none" />
                  </Link>
                ))}
              </div>
            </section>
          )}

          {(d.related_food_items_full || []).length > 0 && (
            <section id="related-foods" className="scroll-mt-24" data-testid="section-related-foods">
              <h2 className="font-display text-2xl mb-4">Foods that contain it</h2>
              <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                {d.related_food_items_full.map((f) => (
                  <Link key={f.slug} to={`/food-pulse/food/${f.slug}`}
                        className="rounded-xl border p-3 bg-white hover:shadow-md transition-all"
                        style={{ borderColor: `${AMBER}55` }}
                        data-testid={`related-food-${f.slug}`}>
                    <div className="font-medium text-sm">{f.common_name}</div>
                    <div className="text-[10px] font-mono uppercase tracking-widest opacity-60">{f.category}</div>
                  </Link>
                ))}
              </div>
            </section>
          )}

          {(d.references || []).length > 0 && (
            <section id="references" className="scroll-mt-24" data-testid="section-references">
              <h2 className="font-display text-2xl mb-4">References</h2>
              <ol className="space-y-2 list-decimal list-inside">
                {d.references.map((ref, i) => (
                  <li key={i} className="text-xs">
                    <span className="opacity-60 font-mono">[{ref.source}]</span>{" "}
                    <span>{ref.title}</span>
                    {ref.url && (
                      <a href={ref.url} target="_blank" rel="noreferrer" className="ml-1 opacity-70 hover:opacity-100 inline-flex items-center gap-1">
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                  </li>
                ))}
              </ol>
            </section>
          )}
        </article>
      </div>
    </div>
  );
}

function Section({ id, title, body, icon: Icon }) {
  return (
    <section id={id} className="scroll-mt-24" data-testid={`section-${id}`}>
      <h2 className="font-display text-2xl mb-4 flex items-baseline gap-2">
        {Icon && <Icon className="w-4 h-4 self-center opacity-70" />}
        {title}
      </h2>
      <div className="prose prose-sm max-w-none opacity-95"
           dangerouslySetInnerHTML={{ __html: body }} />
    </section>
  );
}

/** Safety callout — colour-coded by level. Rendered above-the-fold on
 *  ingredients with a known interaction, restriction, or public-health
 *  advisory (e.g. sodium benzoate + vitamin C → benzene). */
function SafetyCallout({ alert }) {
  const styles = {
    warning: { bg: "#FEF3E7", border: "#F0A356", text: "#7A3E00", label: "Safety Alert" },
    caution: { bg: "#FFF9E6", border: "#E4A140", text: "#6B4A00", label: "Good to Know" },
    info:    { bg: "#EAF2FF", border: "#4E86D6", text: "#1F3A6B", label: "Note" },
  };
  const s = styles[alert.level] || styles.caution;
  return (
    <section className="border-b" style={{ borderColor: `${AMBER}22`, background: "#fff" }}>
      <div className="max-w-5xl mx-auto px-4 py-6">
        <div
          className="rounded-2xl border-2 p-5 sm:p-6 flex gap-4"
          style={{ background: s.bg, borderColor: s.border, color: s.text }}
          data-testid="ingredient-safety-alert"
        >
          <div className="flex-none">
            <div
              className="w-10 h-10 rounded-full flex items-center justify-center"
              style={{ background: s.border, color: "#fff" }}
            >
              <AlertTriangle className="w-5 h-5" />
            </div>
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[10px] font-mono uppercase tracking-widest mb-1"
                 style={{ color: s.border }}>
              {s.label}
            </div>
            {alert.title && (
              <div className="font-display text-lg leading-tight mb-2"
                   data-testid="ingredient-safety-alert-title">
                {alert.title}
              </div>
            )}
            <div
              className="text-sm leading-relaxed"
              dangerouslySetInnerHTML={{ __html: alert.body_html }}
              data-testid="ingredient-safety-alert-body"
            />
          </div>
        </div>
      </div>
    </section>
  );
}

function sectionHasContent(id, d) {
  const M = {
    "summary":              () => !!d.summary,
    "quick-facts":          () => d.quick_facts && Object.values(d.quick_facts).some(Boolean),
    "chemical-structure":   () => !!d.chemical_structure,
    "manufacturing":        () => !!d.manufacturing_process,
    "history":              () => !!d.history,
    "why-used":             () => !!d.why_used,
    "common-foods":         () => (d.common_foods || []).length > 0,
    "health-benefits":      () => !!d.health_benefits,
    "possible-risks":       () => !!d.possible_risks,
    "safe-intake":          () => !!d.safe_intake,
    "regulatory":           () => d.regulatory_status && Object.values(d.regulatory_status).some(Boolean),
    "scientific-research":  () => !!d.scientific_research,
    "public-controversies": () => !!d.public_controversies,
    "environmental-impact": () => !!d.environmental_impact,
    "occupational":         () => !!d.occupational_exposure,
    "animal-studies":       () => !!d.animal_studies,
    "human-studies":        () => !!d.human_studies,
    "labeling":             () => !!d.food_labeling,
    "natural-sources":      () => !!d.natural_sources,
    "myths":                () => (d.common_myths || []).length > 0,
    "faqs":                 () => (d.faqs || []).length > 0,
    "related-ingredients":  () => (d.related_ingredients_full || []).length > 0,
    "related-foods":        () => (d.related_food_items_full || []).length > 0,
    "references":           () => (d.references || []).length > 0,
  };
  return M[id] ? M[id]() : false;
}

function NotFound({ slug }) {
  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ background: CREAM, color: GREEN }}>
      <div className="max-w-md text-center" data-testid="ingredient-not-found">
        <BookOpen className="w-14 h-14 mx-auto opacity-30 mb-3" />
        <h1 className="font-display text-3xl mb-2">No entry for "{slug}"</h1>
        <p className="text-sm opacity-70 mb-6">
          This ingredient isn't in the encyclopedia yet.
          It may be scheduled for the next generation batch.
        </p>
        <Link to="/food-pulse/encyclopedia" className="rounded-full px-6 py-3 text-white font-semibold inline-flex items-center gap-1"
              style={{ background: GREEN }}>
          Browse all ingredients →
        </Link>
      </div>
    </div>
  );
}
